import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  IconButton,
  useTheme,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  CircularProgress,
  Divider,
} from '@mui/material';
import { tokens } from '../../global/theme/tokens';
import { useState, useEffect } from 'react';
import { API_DELETE, API_GET, API_PATCH, API_POST, DBRequest, DBUploadData, useConfig } from '../../api/api';
import { useTranslation } from 'react-i18next';
import ExoAvatar from '../exo/ExoAvatar';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import GroupIcon from '@mui/icons-material/Group';
import TagIcon from '@mui/icons-material/Tag';
import { useChatClient } from './useChatClient';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import Feedback from '../special/Feedback';

export const ChatConversationSettings = ({ open, onClose, conversation }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const config = useConfig();
  const { t } = useTranslation();
  const { updateConversation } = useChatClient();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showAddMember, setShowAddMember] = useState(false);
  const [users, setUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [showAvatarUpload, setShowAvatarUpload] = useState(false);
  const [alertState, setAlertState] = useState({ alertOpen: false, alertType: 'info', alertText: '' });

  const isAdmin = conversation?.currentUserParticipant?.role === 'admin';
  const isGroup = conversation?.type === 'group';
  const isChannel = conversation?.type === 'channel';
  const canEdit = isAdmin && (isGroup || isChannel);

  useEffect(() => {
    if (conversation) {
      setName(conversation.name || '');
      setDescription(conversation.description || '');
    }
  }, [conversation]);

  const handleSave = async () => {
    if (!config || !conversation) return;

    setIsLoading(true);
    DBRequest({
      config,
      path: `chat/conversations/${conversation.id}`,
      method: API_PATCH({ name, description }),
      onResponse: (res) => {
        updateConversation(conversation.id, { name, description });
        setIsLoading(false);
        onClose();
      },
      onError: () => setIsLoading(false),
    });
  };

  const handleRemoveParticipant = (userId) => {
    if (!config || !conversation) return;

    DBRequest({
      config,
      path: `chat/conversations/${conversation.id}/participants/${userId}`,
      method: API_DELETE,
      onResponse: () => {
        const updatedParticipants = conversation.participants?.filter(p => {
          const pUserId = p.user?.id || p.userId;
          return pUserId !== userId;
        });
        updateConversation(conversation.id, { participants: updatedParticipants });
      },
    });
  };

  const handleSearchUsers = (query) => {
    setSearchQuery(query);
    if (!config || !query || query.length < 2) {
      setUsers([]);
      return;
    }

    setIsSearching(true);
    DBRequest({
      config,
      path: `users?search=${encodeURIComponent(query)}&perPage=10`,
      method: API_GET,
      onResponse: (res) => {
        // Filter out users already in conversation
        const existingIds = conversation.participants?.map(p => p.user?.id || p.userId) || [];
        const filteredUsers = (res.data || []).filter(u => !existingIds.includes(u.id));
        setUsers(filteredUsers);
        setIsSearching(false);
      },
      onError: () => setIsSearching(false),
    });
  };

  const handleAddParticipant = (userId) => {
    if (!config || !conversation) return;

    DBRequest({
      config,
      path: `chat/conversations/${conversation.id}/participants`,
      method: API_POST({ user_ids: [userId] }),
      onResponse: (res) => {
        const updatedConversation = res.data || res;
        if (updatedConversation.participants) {
          updateConversation(conversation.id, { participants: updatedConversation.participants });
        }
        setShowAddMember(false);
        setSearchQuery('');
        setUsers([]);
      },
    });
  };

  const handleAvatarUpload = (files) => {
    if (!files || files.length === 0 || !config || !conversation) return;

    const formData = new FormData();
    formData.append('avatar', files[0]);

    DBUploadData({
      config,
      path: `chat/conversations/${conversation.id}/avatar`,
      formData,
      onResponse: (res) => {
        const updatedConversation = res.data || res;
        if (updatedConversation.avatar) {
          updateConversation(conversation.id, { avatar: updatedConversation.avatar });
        }
        setShowAvatarUpload(false);
        setAlertState({ alertOpen: true, alertType: 'success', alertText: t('Avatar updated') });
      },
      onError: (error) => {
        const errorMessage = error?.response?.data?.message || error?.response?.data?.errors?.avatar?.[0] || t('Upload failed');
        setAlertState({ alertOpen: true, alertType: 'error', alertText: errorMessage });
      },
    });
  };

  const getConversationIcon = () => {
    const avatarUrl = conversation?.avatar?.temporaryUrl || conversation?.avatar?.url;

    if (avatarUrl) {
      return (
        <ExoAvatar
          size="80px"
          name={conversation.name || t('Group')}
          picture={avatarUrl}
        />
      );
    }

    if (isChannel) {
      return (
        <Box
          className="flex items-center justify-center rounded-2xl"
          sx={{ width: 80, height: 80, backgroundColor: colors.bgInput }}
        >
          <TagIcon sx={{ color: colors.info, fontSize: 40 }} />
        </Box>
      );
    }

    return (
      <Box
        className="flex items-center justify-center rounded-2xl"
        sx={{ width: 80, height: 80, backgroundColor: colors.bgInput }}
      >
        <GroupIcon sx={{ color: colors.info, fontSize: 40 }} />
      </Box>
    );
  };

  if (!conversation) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: colors.glass,
          backdropFilter: 'blur(10px)',
          borderRadius: '16px',
        },
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6" fontWeight={600}>
          {canEdit ? t('Edit') : t('Info')} {isGroup ? t('Group') : t('Channel')}
        </Typography>
        <IconButton onClick={onClose} size="small" sx={{ color: colors.text, opacity: 0.5 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {/* Avatar */}
        <Box className="flex flex-col items-center mb-4">
          <Box className="relative">
            {getConversationIcon()}
            {canEdit && (
              <IconButton
                size="small"
                onClick={() => setShowAvatarUpload(true)}
                sx={{
                  position: 'absolute',
                  bottom: 0,
                  right: 0,
                  backgroundColor: colors.info,
                  color: '#fff',
                  '&:hover': { backgroundColor: colors.info },
                }}
              >
                <PhotoCameraIcon sx={{ fontSize: 16 }} />
              </IconButton>
            )}
          </Box>
        </Box>

        {/* Name & Description */}
        {canEdit ? (
          <Box className="flex flex-col gap-3 mb-4">
            <TextField
              fullWidth
              label={t('Name')}
              value={name}
              onChange={(e) => setName(e.target.value)}
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: colors.bgInput,
                  borderRadius: '10px',
                },
              }}
            />
            <TextField
              fullWidth
              label={t('Description')}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              size="small"
              multiline
              rows={2}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: colors.bgInput,
                  borderRadius: '10px',
                },
              }}
            />
          </Box>
        ) : (
          <Box className="text-center mb-4">
            <Typography variant="h6" fontWeight={600}>{conversation.name}</Typography>
            {conversation.description && (
              <Typography variant="body2" sx={{ color: colors.text, opacity: 0.6, mt: 1 }}>
                {conversation.description}
              </Typography>
            )}
          </Box>
        )}

        <Divider sx={{ borderColor: colors.card, my: 2 }} />

        {/* Participants */}
        <Box className="flex items-center justify-between mb-2">
          <Typography variant="subtitle2" fontWeight={600}>
            {t('Participants')} ({conversation.participants?.length || 0})
          </Typography>
          {canEdit && (
            <IconButton
              size="small"
              onClick={() => setShowAddMember(!showAddMember)}
              sx={{ color: colors.info }}
            >
              <PersonAddIcon sx={{ fontSize: 20 }} />
            </IconButton>
          )}
        </Box>

        {/* Add Member Search */}
        {showAddMember && canEdit && (
          <Box className="mb-3">
            <TextField
              fullWidth
              size="small"
              placeholder={t('Search users...')}
              value={searchQuery}
              onChange={(e) => handleSearchUsers(e.target.value)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: colors.bgInput,
                  borderRadius: '10px',
                },
              }}
            />
            {isSearching ? (
              <Box className="flex justify-center py-2">
                <CircularProgress size={20} />
              </Box>
            ) : users.length > 0 ? (
              <List dense sx={{ maxHeight: 150, overflow: 'auto' }}>
                {users.map((user) => (
                  <ListItem
                    key={user.id}
                    button
                    onClick={() => handleAddParticipant(user.id)}
                    sx={{ borderRadius: '8px', '&:hover': { backgroundColor: colors.card } }}
                  >
                    <ListItemAvatar sx={{ minWidth: 40 }}>
                      <ExoAvatar
                        size="32px"
                        name={`${user.firstName || ''} ${user.lastName || ''}`}
                        picture={user.picture?.temporaryUrl || user.picture?.url}
                      />
                    </ListItemAvatar>
                    <ListItemText
                      primary={`${user.firstName || ''} ${user.lastName || ''}`}
                      primaryTypographyProps={{ variant: 'body2' }}
                    />
                  </ListItem>
                ))}
              </List>
            ) : searchQuery.length >= 2 ? (
              <Typography variant="caption" sx={{ color: colors.text, opacity: 0.5, display: 'block', textAlign: 'center', py: 1 }}>
                {t('No users found')}
              </Typography>
            ) : null}
          </Box>
        )}

        {/* Participant List */}
        <List sx={{ maxHeight: 200, overflow: 'auto' }}>
          {conversation.participants?.map((participant) => {
            const user = participant.user || participant;
            const userId = user.id || participant.userId;
            const isCurrentUser = userId === conversation.currentUserParticipant?.userId;
            const canRemove = canEdit && !isCurrentUser;

            return (
              <ListItem
                key={participant.id || userId}
                sx={{ borderRadius: '8px', py: 1, mb: 0.5, '&:hover': { backgroundColor: colors.card } }}
              >
                <ListItemAvatar sx={{ minWidth: 40 }}>
                  <ExoAvatar
                    size="32px"
                    name={`${user.firstName || ''} ${user.lastName || ''}`}
                    picture={user.picture?.temporaryUrl || user.picture?.url}
                  />
                </ListItemAvatar>
                <ListItemText
                  primary={`${user.firstName || ''} ${user.lastName || ''}`}
                  secondary={participant.role === 'admin' ? t('Admin') : null}
                  primaryTypographyProps={{ variant: 'body2', fontWeight: isCurrentUser ? 600 : 400 }}
                  secondaryTypographyProps={{ variant: 'caption', sx: { color: colors.info } }}
                />
                {canRemove && (
                  <ListItemSecondaryAction>
                    <IconButton
                      size="small"
                      onClick={() => handleRemoveParticipant(userId)}
                      sx={{ color: colors.error, opacity: 0.6, '&:hover': { opacity: 1 } }}
                    >
                      <DeleteIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </ListItemSecondaryAction>
                )}
              </ListItem>
            );
          })}
        </List>
      </DialogContent>

      {canEdit && (
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={onClose} sx={{ color: colors.text }}>
            {t('Cancel')}
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={isLoading}
            sx={{
              backgroundColor: colors.info,
              '&:hover': { backgroundColor: colors.info },
            }}
          >
            {isLoading ? <CircularProgress size={20} /> : t('Save')}
          </Button>
        </DialogActions>
      )}

      {/* Avatar Upload Dialog */}
      <Dialog
        open={showAvatarUpload}
        onClose={() => setShowAvatarUpload(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: colors.glass,
            backdropFilter: 'blur(10px)',
            borderRadius: '16px',
          },
        }}
      >
        <DialogTitle>{t('Upload Avatar')}</DialogTitle>
        <DialogContent>
          <Box
            className="flex flex-col items-center justify-center p-6 rounded-lg cursor-pointer"
            sx={{
              border: `2px dashed ${colors.info}40`,
              backgroundColor: colors.bgInput,
              '&:hover': { borderColor: colors.info },
            }}
            onClick={() => document.getElementById('avatar-upload-input').click()}
          >
            <CloudUploadIcon sx={{ fontSize: 48, color: colors.info, mb: 2 }} />
            <Typography variant="body2" sx={{ color: colors.text, opacity: 0.7, textAlign: 'center' }}>
              {t('Click to select an image')}
            </Typography>
            <input
              id="avatar-upload-input"
              type="file"
              accept="image/*"
              hidden
              onChange={(e) => handleAvatarUpload(e.target.files)}
            />
          </Box>
        </DialogContent>
      </Dialog>

      <Feedback state={alertState} setState={setAlertState} />
    </Dialog>
  );
};
